import { usePortalMessage, type PortalMessage, resolvePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The People panel — the members of one workspace and its open invitations, with the controls
 * the signed-in role may use and nothing else.
 *
 * Used by Workspace detail and by Workspaces & team (customer-portal review 2026-09-25 #67: the
 * page titled "& team" listed nobody and had no Invite).
 *
 * WHAT CHANGED ON 2026-09-25 (review #132, #134, #135, #142, #145, #154, #158):
 *
 *   · **Only an Owner touches an Owner.** An Admin's role box never offers "Owner", and on an
 *     Owner's row the role is shown as text and Remove is withheld, each with the reason. The API
 *     (`requireOwnerCaller`) and the database (0031) refuse it too; this is what is OFFERED.
 *   · **A Member or a Viewer sees the list read-only**: no invite form, no role boxes, no Remove,
 *     no Revoke — one line says who can.
 *   · **The only Owner's role cannot be changed from here**, and they cannot be removed: the box is
 *     disabled with the reason, rather than letting a click lock the account out.
 *   · **Recorded facts only.** "Last active" shows a time when one was recorded and a dash when
 *     not, never "never"; the "Email unconfirmed" tag is gone, because WeTalk sends no confirmation
 *     email, so it was true of everyone and meant nothing.
 *   · **Remove asks first**, naming the person and their role. A role change says "Saved".
 *
 * AMENDED 2026-09-25 (package M — review #142, #143, #144, #152, #156, #158):
 *
 *   · **Revoke asks first**, naming the address, because the link it ends cannot be sent again.
 *   · **A role change that takes rights away asks first**, naming what is lost ("Make Nikos an
 *     Admin? They will no longer be able to manage billing."); your own demotion says "you".
 *   · **The invitation link has a Copy button** ("Copied"), and the panel says plainly that WeTalk
 *     does not email it: the Owner sends it themselves.
 *   · **A refusal shows its request id** when WeTalk itself failed (`apiProblemText`).
 *   · **At phone width the table is stacked rows** (`.c-stack-table`), so no role box is squeezed
 *     to nothing and the page never scrolls sideways.
 *
 * AMENDED 2026-10-06 (plan-014 B3, FR12-TEAM-05):
 *
 *   · **Seven roles.** The invite form offers Admin, Member, Viewer, Developer, Campaign manager and Billing (and Owner
 *     to an Owner); a role box offers the same.
 *   · **Your role in THIS workspace decides what is offered** (`callerRole`): on another workspace's page the controls
 *     follow the role you hold there, never the one you happen to be working in — the API judges the same target.
 *   · **Pending, expired and revoked invitations are shown apart** from the members: pending ones in the table with
 *     Revoke, the others listed below it, for the record.
 *
 * AMENDED 2026-10-09 (ADR 0041): the organization's Owner and Admin read the people of every workspace, member there
 * or not. Holding no role HERE, they see the list read-only, with one line saying why and who changes it — inviting,
 * changing a role and removing stay with the workspace's own Owners and Admins (the API refuses them too).
 *
 * AMENDED 2026-10-10 (ADR 0052, the owner's decision of 2026-10-10): the organization's Owner and Admin change roles,
 * remove people and withdraw invitations here too, member here or not — the role boxes (every role), Remove and Revoke,
 * each row judged by `peopleChangeRefusal` (the capability module's rule, as the route's: the last Owner reachable by
 * the organization, never their own role). Inviting stays with the workspace's own Owners and Admins, so no Invite
 * form; one line says so. The organization's Admin on the organization's Owner is refused by the API, by name.
 *
 * A NAME CAN BE NULL: `membership` is tenant-scoped and `person` is behind the identity role, so a
 * membership can outlive the person row it points at. The row renders the gap.
 */
import { assignableMemberRole, isOwnerRole } from "@wetalk/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  Input,
  Mono,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authSessionQuery, workspacePeopleQuery } from "../../api/query.js";
import { apiProblemText } from "../../api/api-error.js";
import {
  assignableRole,
  assignableRoleFor,
  consoleCan,
  memberCan,
  peopleChangeRefusal,
  useMemberCan,
  whyNot,
  type ConsoleRoles,
} from "../../shell/member-capability.js";
import type {
  IssuedInvitationView,
  MemberRoleView,
  WorkspaceInvitationView,
  WorkspaceMemberView,
} from "../../api/gateway.js";
import { removesRights, rightsLostBy, roleWithArticle } from "./role-change.js";

// `.c-once` — the shown-once callout — lives with Billing, the unit that introduced it.
import "../billing/billing.css";
import "./workspaces.css";

/** The seven workspace roles, in the order the matrix lists them (design §27.5.3; plan-014 B3). */
export const ROLE: readonly { readonly value: MemberRoleView; readonly label: string }[] = [
  { value: "owner", get label() { return portalText("core", "account_admin.owner_89ff31"); } },
  { value: "admin", get label() { return portalText("core", "account_admin.admin_4e7afe"); } },
  { value: "member", get label() { return portalText("core", "account_admin.member_6853c9"); } },
  { value: "viewer", get label() { return portalText("core", "account_admin.viewer_dbad9f"); } },
  { value: "developer", get label() { return portalText("core", "account_admin.developer_7e08eb"); } },
  { value: "campaign_manager", get label() { return portalText("core", "account_admin.campaign_manager_ce843f"); } },
  { value: "billing", get label() { return portalText("core", "account_admin.billing_abaec4"); } },
];

export function roleLabel(role: MemberRoleView): string {
  return ROLE.find((one) => one.value === role)?.label ?? role;
}

function nameOf(member: WorkspaceMemberView): string {
  return member.displayName ?? member.emailAddress ?? portalText("core", "account_admin.this_person_685984");
}

export interface PeoplePanelProps {
  readonly workspaceId: string;
  /** The workspace's name, for the Remove confirmation. */
  readonly workspaceName: string;
  /**
   * The signed-in person's role in THIS workspace, or `null` where they hold none (plan-014 B3): what is offered
   * follows it, as the API's target authorization does.
   */
  readonly callerRole: MemberRoleView | null;
}

export function PeoplePanel({ workspaceId, workspaceName, callerRole }: PeoplePanelProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const people = useQuery(workspacePeopleQuery(gateway, workspaceId));

  const [emailAddress, setEmailAddress] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRoleView>("member");
  const [issued, setIssued] = useState<IssuedInvitationView | null>(null);
  const [message, setMessage] = usePortalMessage();
  const [saved, setSaved] = usePortalMessage();
  const [removing, setRemoving] = useState<WorkspaceMemberView | null>(null);
  const [revoking, setRevoking] = useState<WorkspaceInvitationView | null>(null);
  const [demoting, setDemoting] = useState<{
    readonly one: WorkspaceMemberView;
    readonly role: MemberRoleView;
  } | null>(null);
  const signedInPersonId = useQuery(authSessionQuery(gateway)).data?.person.personId ?? null;
  // ADR 0041 / 0052: the organization's Owner or Admin reads, changes and removes the people of every workspace.
  const accountRole = useMemberCan().accountRole;
  const roles: ConsoleRoles = { memberRole: callerRole, accountRole };

  const refreshPeople = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspacePeople });
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
  };

  const failed = (fallback: PortalMessage) => (error: unknown) => {
    setSaved(null);
    setMessage(() => (apiProblemText(error, resolvePortalMessage(fallback))));
  };

  const invite = useMutation({
    mutationFn: () => gateway.inviteToWorkspace(workspaceId, { emailAddress, role: inviteRole }),
    onSuccess: async (made) => {
      setIssued(made);
      setEmailAddress("");
      setMessage(null);
      await refreshPeople();
    },
    onError: failed(() => portalText("core", "workspaces.that_invitation_could_not_be_sent_b0455d")),
  });

  const changeRole = useMutation({
    mutationFn: (input: { readonly one: WorkspaceMemberView; readonly role: MemberRoleView }) =>
      gateway.setMemberRole(workspaceId, input.one.membershipId, input.role),
    onSuccess: async (_changed, input) => {
      setDemoting(null);
      setMessage(null);
      setSaved(() => (portalText("core", "workspaces.saved_is_now_661746", { value0: nameOf(input.one), value1: roleWithArticle(input.role) })));
      await refreshPeople();
    },
    onError: (error: unknown) => {
      setDemoting(null);
      failed(() => portalText("core", "account_admin.that_role_could_not_be_changed_ffddc0"))(error);
    },
  });

  const remove = useMutation({
    mutationFn: (one: WorkspaceMemberView) => gateway.removeMember(workspaceId, one.membershipId),
    onSuccess: async (_done, one) => {
      setRemoving(null);
      setMessage(null);
      setSaved(() => (portalText("core", "workspaces._was_removed_from_d8c626", { value0: nameOf(one), value1: workspaceName })));
      await refreshPeople();
    },
    onError: (error: unknown) => {
      setRemoving(null);
      failed(() => portalText("core", "workspaces.that_person_could_not_be_removed_ff0f8a"))(error);
    },
  });

  const revoke = useMutation({
    mutationFn: (pending: WorkspaceInvitationView) => gateway.revokeInvitation(workspaceId, pending.invitationId),
    onSuccess: async (_done, pending) => {
      setRevoking(null);
      setMessage(null);
      setSaved(() => (portalText("core", "workspaces.the_invitation_to_was_withdrawn_its_link_no_longer_works_881b4a", { value0: pending.emailAddress })));
      await refreshPeople();
    },
    onError: (error: unknown) => {
      setRevoking(null);
      failed(() => portalText("core", "workspaces.that_invitation_could_not_be_withdrawn_2407b7"))(error);
    },
  });

  /** A change that takes rights away is asked about first (review #158); any other is saved. */
  const askOrChange = (one: WorkspaceMemberView, role: MemberRoleView): void => {
    setSaved(null);
    if (removesRights(one.role, role)) setDemoting({ one, role });
    else changeRole.mutate({ one, role });
  };

  const canInvite = callerRole !== null && memberCan(callerRole, "membership.invite");
  // ADR 0052: by the workspace role, or by the organization's Owner or Admin (the bridged capabilities).
  const canRemove = consoleCan(roles, "membership.remove");
  const managesPeople = consoleCan(roles, "membership.role_change") || canRemove;
  // Withdrawing an invitation: whoever invites, or whoever removes (the organization's Owner and Admin, ADR 0052).
  const canWithdraw = canInvite || canRemove;
  // Holding no role here, the organization's Owner or Admin manages the people by the organization's authority.
  const byOrganization = callerRole === null && managesPeople;
  const pendingInvitation = people.data?.invitation.filter((one) => one.state === "pending") ?? [];
  const pastInvitation = people.data?.invitation.filter((one) => one.state !== "pending") ?? [];
  const ownerCount = people.data?.member.filter((one) => isOwnerRole(one.role)).length ?? 0;
  // An Admin may not invite an Owner (the API refuses it); the box offers what the role can give.
  const invitable = callerRole === null ? [] : assignableRole(callerRole);

  return (
    <div className="c-people">
      <div className="c-row-spaced c-row c-people-head">
        <p className="c-section-title">{portalText("core", "account_admin.people_b37554")}</p>
        {canInvite ? (
          <form
            className="c-row"
            onSubmit={(event) => {
              event.preventDefault();
              setSaved(null);
              invite.mutate();
            }}
          >
            <Input
              type="email"
              required
              placeholder={portalText("core", "workspaces.colleague_example_com_69770c")}
              aria-label={portalText("core", "workspaces.email_address_to_invite_e56d00")}
              value={emailAddress}
              onChange={(event) => {
                setEmailAddress(event.currentTarget.value);
              }}
            />
            <Select
              aria-label={portalText("core", "workspaces.role_for_the_invitation_f26a81")}
              value={inviteRole}
              onChange={(event) => {
                setInviteRole(event.currentTarget.value as MemberRoleView);
              }}
            >
              {ROLE.filter((each) => invitable.includes(each.value)).map((each) => (
                <option key={each.value} value={each.value}>
                  {each.label}
                </option>
              ))}
            </Select>
            <Button variant="primary" type="submit" disabled={invite.isPending}>{portalText("core", "account_admin.invite_b13660")}</Button>
          </form>
        ) : null}
      </div>

      {callerRole !== null && !managesPeople ? (
        <p className="c-note">
          {whyNot(callerRole, "membership.invite")}{" "}{portalText("core", "workspaces.only_they_can_change_roles_or_remove_people_e74bce")}</p>
      ) : null}
      {byOrganization ? (
        <p className="c-note">
          {accountRole === "owner"
            ? portalText("core", "workspaces.you_have_no_role_in_this_workspace_as_the_organization_s_owner_yo_e1adc3")
            : portalText("core", "workspaces.you_have_no_role_in_this_workspace_as_the_organization_s_admin_yo_c45250")}
        </p>
      ) : null}

      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
      {saved === null ? null : (
        <p className="c-note" role="status">
          {saved}
        </p>
      )}

      {people.data === undefined ? (
        people.isError ? (
          <p className="c-note c-note-critical">{portalText("core", "workspaces.the_people_in_this_workspace_could_not_be_read_7e5979")}{" "}
            <Button
              variant="ghost"
              onClick={() => {
                void people.refetch();
              }}
            >{portalText("core", "workspaces.try_again_042c86")}</Button>
          </p>
        ) : (
          <p className="c-note">{portalText("core", "workspaces.reading_the_people_in_this_workspace_7aa6df")}</p>
        )
      ) : (
        <div className="c-people-scroll c-stack-table">
          <Table caption={portalText("core", "workspaces.people_in_this_workspace_and_invitations_not_yet_accepted_028d71")}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("core", "workspaces.name_709a23")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "auth.email_84add5")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "workspaces.role_c3f104")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "workspaces.last_active_b5665e")}</TableHeaderCell>
                <TableHeaderCell>{portalText("core", "dashboard.status_bae7d5")}</TableHeaderCell>
                {managesPeople || canWithdraw ? <TableHeaderCell>{""}</TableHeaderCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {people.data.member.map((one) => {

                const lastOwner = isOwnerRole(one.role) && ownerCount <= 1;
                const self = one.personId === signedInPersonId;
                // The role's own refusal first ("Only an Owner can…"), then the last-Owner rule — which the
                // organization's Owner and Admin are not held to (ADR 0052).
                const roleRefusal = managesPeople
                  ? peopleChangeRefusal(roles, {
                      target: one.role,
                      act: "membership.role_change",
                      self,
                      lastOwner,
                      lastOwnerText: portalText("core", "workspaces.the_only_owner_cannot_change_role_make_someone_else_an_owner_firs_f6320f"),
                    })
                  : null;
                const removeRefusal = canRemove
                  ? peopleChangeRefusal(roles, {
                      target: one.role,
                      act: "membership.remove",
                      self,
                      lastOwner,
                      lastOwnerText: portalText("core", "workspaces.the_only_owner_cannot_be_removed_make_someone_else_an_owner_first_d597fd"),
                    })
                  : null;
                // Your own row offers what your role HERE may give (nobody changes their own role by the organization's).
                const giveable = self ? assignableMemberRole(callerRole) : assignableRoleFor(roles);
                const options = managesPeople ? ROLE.filter((each) => each.value === one.role || giveable.includes(each.value)) : [];
                return (
                  <TableRow key={one.membershipId}>
                    <TableCell>{one.displayName ?? portalText("core", "workspaces.this_person_s_account_has_been_closed_366a1e")}</TableCell>
                    <TableCell>{one.emailAddress ?? "—"}</TableCell>
                    <TableCell>
                      {managesPeople ? (
                        <Select
                          aria-label={portalText("core", "workspaces.role_for_ab001a", { value0: nameOf(one) })}
                          value={one.role}
                          disabled={roleRefusal !== null || changeRole.isPending}
                          title={roleRefusal ?? undefined}
                          onChange={(event) => {
                            askOrChange(one, event.currentTarget.value as MemberRoleView);
                          }}
                        >
                          {options.map((each) => (
                            <option key={each.value} value={each.value}>
                              {each.label}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        roleLabel(one.role)
                      )}
                    </TableCell>
                    <TableCell>{one.lastActiveText ?? "—"}</TableCell>
                    <TableCell>
                      <Tag status="positive">{portalText("core", "workspaces.joined_43a1c6")}</Tag>
                    </TableCell>
                    {managesPeople || canWithdraw ? (
                      <TableCell>
                        {canRemove ? (
                          <Button
                            variant="ghost"
                            disabled={removeRefusal !== null}
                            title={removeRefusal ?? undefined}
                            aria-label={portalText("core", "workspaces.remove_494593", { value0: nameOf(one) })}
                            onClick={() => {
                              setRemoving(one);
                            }}
                          >{portalText("core", "account_admin.remove_e96390")}</Button>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
              {pendingInvitation.map((pending) => (
                <TableRow key={pending.invitationId}>
                  <TableCell>—</TableCell>
                  <TableCell>{pending.emailAddress}</TableCell>
                  <TableCell>{roleLabel(pending.role)}</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>
                    <Tag status="caution">{portalText("core", "workspaces.invited_53469d")}</Tag> <span className="c-people-expiry">{portalText("core", "workspaces.expires_a99be3")}{" "}{pending.expiresAtText}</span>
                  </TableCell>
                  {managesPeople || canWithdraw ? (
                    <TableCell>
                      {canWithdraw ? (
                        <Button
                          variant="ghost"
                          aria-label={portalText("core", "workspaces.withdraw_the_invitation_to_3e0951", { value0: pending.emailAddress })}
                          onClick={() => {
                            setRevoking(pending);
                          }}
                        >{portalText("core", "account_admin.revoke_0be720")}</Button>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pastInvitation.length === 0 ? null : (
            <div className="c-ws-past-invitation">
              <p className="c-ws-past-invitation-title">{portalText("core", "workspaces.expired_and_revoked_invitations_054963")}</p>
              <ul className="c-ws-past-invitation-list">
                {pastInvitation.map((past) => (
                  <li key={past.invitationId} className="c-ws-past-invitation-row">
                    <span className="c-ws-past-invitation-email">{past.emailAddress}</span>
                    <span>{roleLabel(past.role)}</span>
                    <Tag status="muted">{past.state === "expired" ? portalText("core", "account_admin.expired_a689a9") : portalText("core", "account_admin.revoked_85f17a")}</Tag>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={removing !== null}
        title={removing === null ? portalText("core", "account_admin.remove_e96390") : portalText("core", "workspaces.remove_b28967", { value0: nameOf(removing) })}
        onClose={() => {
          setRemoving(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setRemoving(null);
              }}
            >{portalText("core", "workspaces.keep_them_40343d")}</Button>
            <Button
              variant="primary"
              disabled={remove.isPending}
              onClick={() => {
                if (removing !== null) remove.mutate(removing);
              }}
            >{portalText("core", "account_admin.remove_e96390")}</Button>
          </>
        }
      >
        {removing === null ? null : (
          <p className="c-note">
            {nameOf(removing)} ({roleLabel(removing.role)}{portalText("core", "workspaces._loses_access_to_3e8b25")}{" "}{workspaceName}{" "}{portalText("core", "workspaces.at_once_and_is_signed_out_of_it_you_can_invite_them_again_later_0e624e")}</p>
        )}
      </Dialog>

      <Dialog
        open={revoking !== null}
        title={revoking === null ? portalText("core", "account_admin.revoke_0be720") : portalText("core", "workspaces.revoke_the_invitation_to_5b6b49", { value0: revoking.emailAddress })}
        onClose={() => {
          setRevoking(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setRevoking(null);
              }}
            >{portalText("core", "workspaces.keep_it_d9f369")}</Button>
            <Button
              variant="primary"
              disabled={revoke.isPending}
              onClick={() => {
                if (revoking !== null) revoke.mutate(revoking);
              }}
            >{portalText("core", "account_admin.revoke_0be720")}</Button>
          </>
        }
      >
        {revoking === null ? null : (
          <p className="c-note">{portalText("core", "workspaces.the_link_you_sent_to_58c7ef")}{" "}{revoking.emailAddress}{" "}{portalText("core", "workspaces.stops_working_at_once_to_invite_them_later_send_a_new_invitation_b5d4b8")}</p>
        )}
      </Dialog>

      <Dialog
        open={demoting !== null}
        title={
          demoting === null
            ? portalText("core", "account_admin.change_role_2b9bfd")
            : demoting.one.personId === signedInPersonId
              ? portalText("core", "workspaces.make_yourself_49ecca", { value0: roleWithArticle(demoting.role) })
              : portalText("core", "workspaces.make_e435c6", { value0: nameOf(demoting.one), value1: roleWithArticle(demoting.role) })
        }
        onClose={() => {
          setDemoting(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setDemoting(null);
              }}
            >{portalText("core", "workspaces.keep_466fc4")}{" "}{demoting === null ? portalText("core", "workspaces.the_role_fd65e9") : roleWithArticle(demoting.one.role).replace(/^an? /, "")}
            </Button>
            <Button
              variant="primary"
              disabled={changeRole.isPending}
              onClick={() => {
                if (demoting !== null) changeRole.mutate(demoting);
              }}
            >{portalText("core", "account_admin.change_role_2b9bfd")}</Button>
          </>
        }
      >
        {demoting === null ? null : (
          <p className="c-note">
            {demoting.one.personId === signedInPersonId ? portalText("core", "workspaces.you_905cb3") : portalText("core", "workspaces.they_105068")}{" "}{portalText("core", "workspaces.will_no_longer_be_able_to_0d7c46")}{" "}
            {rightsLostBy(demoting.one.role, demoting.role)}
            {demoting.one.personId === signedInPersonId ? portalText("core", "workspaces._and_only_another_owner_or_admin_can_give_that_back_24abe1") : ""}.
          </p>
        )}
      </Dialog>

      <InvitationIssuedDialog
        issued={issued}
        onClose={() => {
          setIssued(null);
        }}
      />
    </div>
  );
}

/**
 * The invitation link, shown once.
 *
 * `invitation.token_hash` is all the database keeps, so this dialog is the only moment the
 * link exists. It is styled as a caution rather than as a success because closing it without
 * copying is the failure mode, and the colour should say so.
 */
function InvitationIssuedDialog({
  issued,
  onClose,
}: {
  readonly issued: IssuedInvitationView | null;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const [copied, setCopied] = useState<"no" | "yes" | "failed">("no");
  // The token rides in the fragment, which no server ever receives (design §16.37).
  const link = issued === null ? "" : `${window.location.origin}/join#token=${issued.token}`;
  const close = (): void => {
    setCopied("no");
    onClose();
  };
  return (
    <Dialog
      open={issued !== null}
      title={portalText("core", "workspaces.invitation_ready_send_this_link_6ec23c")}
      onClose={close}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard
                .writeText(link)
                .then(() => {
                  setCopied("yes");
                })
                .catch(() => {
                  setCopied("failed");
                });
            }}
          >
            {copied === "yes" ? portalText("core", "workspaces.copied_8e3df4") : portalText("core", "account_admin.copy_link_2f84ee")}
          </Button>
          <Button variant="primary" onClick={close}>{portalText("core", "first_run.done_e9b450")}</Button>
        </>
      }
    >
      {issued === null ? null : (
        <>
          <p className="c-note">{portalText("core", "workspaces.wetalk_does_not_email_invitations_yet_so_send_this_link_to_7f857a")}{" "}{issued.emailAddress}{" "}{portalText("core", "workspaces.yourself_by_email_or_chat_opening_it_joins_them_as_da7dc0")}{" "}{roleLabel(issued.role)}{portalText("core", "workspaces._it_works_once_and_expires_741981")}{" "}{issued.expiresAtText}.
          </p>
          {copied === "failed" ? (
            <p className="c-note c-note-critical" role="alert">{portalText("core", "workspaces.this_browser_did_not_let_the_page_copy_it_select_the_link_and_cop_859eaa")}</p>
          ) : null}
          <div className="c-once">
            <span className="c-setting-title">{portalText("core", "workspaces.this_is_shown_once_8be9e3")}</span>
            <span className="c-once-value">
              <Mono wrap>{link}</Mono>
            </span>
            <span className="c-setting-dek">{portalText("core", "workspaces.only_a_digest_of_it_is_stored_so_it_cannot_be_shown_again_if_it_i_00aca3")}</span>
          </div>
        </>
      )}
    </Dialog>
  );
}
